import { useState } from "react";
import { Outlet, Link } from "react-router-dom";
import "./theme.css";
import { useTheme } from "../../context/theme";
import DWLogo from "./dwLogo";
import { Menu as MenuIcon, X as CloseIcon } from "lucide-react";
import { useTranslation } from "../../i18n/useTranslation";

/*
  Secondary navigation — docks beneath the persistent Tapaswe Global
  header. The DeviceWorks logo is the "home" anchor for this section
  (→ /solutions/deviceworks). No theme toggle here — the global nav's
  toggle controls light/dark for the whole site, DeviceWorks included
  (see data-dw-theme below, which just mirrors the global theme).
  Language works the same way — DeviceWorks reads the same shared
  locale context as the parent site, no separate i18n system.

  Whole bar — logo included — is right-aligned as one cluster, so it
  doesn't visually stack under the global logo above it.
*/
const DWSecondaryNav = () => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const NAV_LINKS = [
    { to: "/solutions/deviceworks/marketplace", label: t("deviceworks.navMarketplace") },
    { to: "/solutions/deviceworks/marketplace/compare", label: t("deviceworks.navCompare") },
    { to: "/solutions/deviceworks/lifecycle-services", label: t("deviceworks.navLifecycle") },
    { to: "/solutions/deviceworks/how-we-work", label: t("deviceworks.navHowWeWork") },
    { to: "/solutions/deviceworks/get-a-quote", label: t("deviceworks.navGetAQuote") },
  ];

  return (
    <div className="dw-panel-bg border-b" style={{ borderColor: "var(--dw-line)" }}>
      <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-end gap-8">
        <Link
          to="/solutions/deviceworks"
          onClick={() => setMobileOpen(false)}
          aria-label="DeviceWorks home"
        >
          <DWLogo size={24} />
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="dw-mono text-xs uppercase tracking-wide"
              style={{ color: "var(--dw-text)" }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex md:hidden items-center gap-2">
          <button
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((o) => !o)}
            style={{ color: "var(--dw-text)" }}
          >
            {mobileOpen ? <CloseIcon size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && <MobileMenu NAV_LINKS={NAV_LINKS} onNavigate={() => setMobileOpen(false)} />}
    </div>
  );
};

const MobileMenu = ({ NAV_LINKS, onNavigate }) => (
  <nav className="md:hidden flex flex-col items-end px-5 pb-5 gap-1" style={{ borderTop: "1px solid var(--dw-line)" }}>
    {NAV_LINKS.map((l) => (
      <Link
        key={l.to}
        to={l.to}
        className="py-3 dw-mono text-xs uppercase"
        onClick={onNavigate}
        style={{ color: "var(--dw-text)" }}
      >
        {l.label}
      </Link>
    ))}
  </nav>
);

// The global footer (rendered once by the parent Layout) is the
// single, constant footer for the entire site — DeviceWorks doesn't
// ship a separate footer of its own.
const DWSection = () => {
  const { theme } = useTheme();
  return (
    <div data-dw-theme={theme} className="dw-shell">
      <DWSecondaryNav />
      <main className="dw-bg-base">
        <Outlet />
      </main>
    </div>
  );
};

export default DWSection;
